import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  AccessTime,
  Person,
  Camera,} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import type { NotificationData, NotificationResponse, NotificationStatus } from '../types/notification';
import NotificationDetailModal from '../components/NotificationDetailModal';
import { notificationAPI, isRequestCanceled } from '../utils/api';
import { useListRequest } from '../hooks/useListRequest';
import { buildNotificationImageUrl } from '../utils/notificationImageUrl';
import { useAuth } from '../hooks/useAuth';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';
import { formatActionTypeLabel } from '../utils/formatActionType';
import { Container } from 'react-bootstrap';

// Helper function to convert API response to component format (same as NotificationBell)
const convertAPINotificationToComponentFormat = (notification: NotificationResponse, companyId: string, timezone: string): NotificationData => {
  const imageUrl = buildNotificationImageUrl(companyId, notification.image_id, notification.camera_number);
  const status = notification.status || (notification.read_flag === 1 ? 'read' : 'unread');
  
  return {
    id: notification.id,
    employeeName: notification.employee_name,
    employeeId: notification.employee_emp_id,
    department: notification.department_name,
    camera: notification.camera_name,
    imageUrl: imageUrl,
    policyName: notification.policy_name,
    alertLevel: notification.alert_level,
    isRead: notification.read_flag === 1 || status !== 'unread',
    similarity: notification.similarity ? notification.similarity * 100 : undefined,
    detectionTime: formatDateTimeInCompanyTimezone(notification.log_time, timezone || 'UTC'),
    title: notification.title,
    message: notification.message,
    actionType: notification.action_type,
    lastSeenAt: notification.last_seen_at,
    minutesAbsent: notification.minutes_absent,
    createdAt: notification.created_at,
    coordinates: notification.coordinates,
    status,
    acknowledgedAt: notification.acknowledged_at ?? null,
    acknowledgedBy: notification.acknowledged_by ?? null,
    resolvedAt: notification.resolved_at ?? null,
    resolvedBy: notification.resolved_by ?? null,
    resolutionNote: notification.resolution_note ?? null,
  };
};

const Notifications: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'notifications' });
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<NotificationData | null>(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(true); // Default to unread-only (avoids slow full-scan query and timeout)
  const [statusFilter, setStatusFilter] = useState<NotificationStatus | 'all'>('unread');
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolvingNotification, setResolvingNotification] = useState<NotificationData | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const { company_id } = useParams<{ company_id: string }>();
  const { user, currentCompany } = useAuth();

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(30);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Get company ID from route params or currentCompany
  const companyId = company_id || currentCompany?.company_id;

  // Each fetch owns its signal; a newer fetch or unmount cancels the superseded request.
  const { begin } = useListRequest(String(companyId));

  // When company changes, clear list and reset filters/pagination
  useEffect(() => {
    if (!companyId) return;
    setNotifications([]);
    setError(null);
    setCurrentPage(1);
    setShowUnreadOnly(true);
    setStatusFilter('unread');
    setPerPage(30);
  }, [companyId]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Fetch notifications with pagination
  const fetchNotifications = useCallback(async (page: number = 1, unreadOnly: boolean = false, itemsPerPage: number = 30, status: NotificationStatus | 'all' = 'all') => {
    if (!user?.id || !companyId) return;

    const controller = begin();
    setLoading(true);
    setError(null);
    try {
      const response = await notificationAPI.getNotifications(companyId, user.id, {
        unread_only: unreadOnly,
        page: page,
        per_page: itemsPerPage,
        status
      }, controller.signal);
      if (controller.signal.aborted) return;
      if (response.success) {
        const convertedNotifications = response.data.notifications.map(n => 
          convertAPINotificationToComponentFormat(n, companyId, currentCompany?.timezone || 'UTC')
        );
        setNotifications(convertedNotifications);
        
        // Update pagination state from response.data.pagination
        const pagination = response.data.pagination;
        if (pagination) {
          setCurrentPage(pagination.current_page);
          setTotalPages(pagination.last_page);
          setTotalCount(pagination.total);
          setPerPage(pagination.per_page);
        }
      }
    } catch (err: any) {
      if (controller.signal.aborted || isRequestCanceled(err)) return;
      console.error('Failed to fetch notifications:', err);
      
      // Show timeout-specific error message
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError(t('timeoutError'));
      } else {
        setError(t('loadFailed'));
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [user?.id, companyId, begin]);

  // Fetch notifications on mount and when filter/pagination changes
  useEffect(() => {
    fetchNotifications(currentPage, showUnreadOnly, perPage, statusFilter);
  }, [fetchNotifications, currentPage, showUnreadOnly, perPage, statusFilter]);

  // Reset to page 1 when filter changes
  const handleFilterChange = () => {
    setCurrentPage(1);
    setShowUnreadOnly(!showUnreadOnly);
    setStatusFilter(showUnreadOnly ? 'all' : 'unread');
  };

  // Handle page change
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle per page change
  const handlePerPageChange = (event: any) => {
    setPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page
  };

  const getAlertLevelColor = (level: string | null) => {
    switch (level) {
      case 'red':
        return 'error';
      case 'yellow':
        return 'warning';
      case 'green':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: NotificationStatus) => {
    switch (status) {
      case 'resolved':
        return 'success';
      case 'acknowledged':
        return 'info';
      case 'read':
        return 'default';
      default:
        return 'error';
    }
  };

  const updateNotification = (id: number, updates: Partial<NotificationData>) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, ...updates } : n));
    setSelectedNotification(prev => prev?.id === id ? { ...prev, ...updates } : prev);
  };

  const handleAcknowledge = async (notification: NotificationData, event?: React.MouseEvent) => {
    event?.stopPropagation();
    try {
      const response = await notificationAPI.acknowledgeNotification(notification.id);
      updateNotification(notification.id, {
        status: response.data?.status || 'acknowledged',
        isRead: true,
        acknowledgedAt: response.data?.acknowledged_at ?? new Date().toISOString(),
        acknowledgedBy: response.data?.acknowledged_by ?? notification.acknowledgedBy,
      });
    } catch (err) {
      console.error('Failed to acknowledge notification:', err);
      setError(t('acknowledgeFailed', { defaultValue: 'Failed to acknowledge notification.' }));
    }
  };

  const handleResolveClick = (notification: NotificationData, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setResolvingNotification(notification);
    setResolutionNote(notification.resolutionNote || '');
    setResolveDialogOpen(true);
  };

  const handleResolveSubmit = async () => {
    if (!resolvingNotification) return;
    try {
      const response = await notificationAPI.resolveNotification(resolvingNotification.id, resolutionNote || undefined);
      updateNotification(resolvingNotification.id, {
        status: response.data?.status || 'resolved',
        isRead: true,
        resolvedAt: response.data?.resolved_at ?? new Date().toISOString(),
        resolvedBy: response.data?.resolved_by ?? resolvingNotification.resolvedBy,
        resolutionNote: response.data?.resolution_note ?? (resolutionNote || null),
      });
      setResolveDialogOpen(false);
      setResolvingNotification(null);
      setResolutionNote('');
    } catch (err) {
      console.error('Failed to resolve notification:', err);
      setError(t('resolveFailed', { defaultValue: 'Failed to resolve notification.' }));
    }
  };

  const formatWorkflowDateTime = (value: string | null) => {
    if (!value) return '';
    return formatDateTimeInCompanyTimezone(value, currentCompany?.timezone || 'UTC');
  };

  const handleNotificationClick = async (notification: NotificationData) => {
    const readNotification: NotificationData = {
      ...notification,
      isRead: true,
      status: notification.status === 'unread' ? 'read' : notification.status,
    };
    setSelectedNotification(readNotification);
    setNotificationModalOpen(true);
    
    // Mark as read locally (optimistic update)
    setNotifications(prev =>
      prev.map(n => n.id === notification.id ? {
        ...n,
        isRead: true,
        status: n.status === 'unread' ? 'read' : n.status,
      } : n)
    );
    
    // Mark as read on server
    try {
      await notificationAPI.markAsRead(notification.id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Revert optimistic update on error
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, isRead: notification.isRead } : n)
      );
      setSelectedNotification(notification);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id || !companyId) return;
    
    // Store original state for rollback
    const originalNotifications = [...notifications];
    
    // Mark all as read locally (optimistic update)
    setNotifications(prev => prev.map(n => ({
      ...n,
      isRead: true,
      status: n.status === 'unread' ? 'read' : n.status,
    })));
    
    // Mark all as read on server
    try {
      const response = await notificationAPI.markAllAsRead(companyId, user.id);
      console.log(`Marked ${response.data.updated_count} notifications as read`);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      // Revert optimistic update on error
      setNotifications(originalNotifications);
      setError(t('markAllFailed'));
    }
  };


  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
      
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <NotificationsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {t('title')}
            </Typography>
            {!loading && unreadCount > 0 && (
              <Chip
                label={t('unread', { count: unreadCount })}
                color="error"
                size="small"
              />
            )}
            {!loading && totalCount > 0 && (
              <Chip
                label={t('total', { count: totalCount })}
                color="default"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            {!loading && unreadCount > 0 && (
              <Button
                variant="outlined"
                startIcon={<i className="bi bi-envelope-check"></i>}
                onClick={handleMarkAllAsRead}
              >
                {t('markAllAsRead')}
              </Button>
            )}
            {!loading && (
              <Button
                variant={showUnreadOnly ? 'contained' : 'outlined'}
                onClick={handleFilterChange}
              >
                {showUnreadOnly ? t('showAll') : t('showUnreadOnly')}
              </Button>
            )}
            {!loading && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{t('perPage')}</InputLabel>
                <Select
                  value={perPage}
                  label={t('perPage')}
                  onChange={handlePerPageChange}
                >
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            )}
            {!loading && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('filterByStatus')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('filterByStatus')}
                  onChange={(event) => {
                    const nextStatus = event.target.value as NotificationStatus | 'all';
                    setStatusFilter(nextStatus);
                    setShowUnreadOnly(nextStatus === 'unread');
                    setCurrentPage(1);
                  }}
                >
                  <MenuItem value="all">{t('allStatuses', { defaultValue: 'All' })}</MenuItem>
                  <MenuItem value="unread">{t('statusUnread')}</MenuItem>
                  <MenuItem value="read">{t('statusRead')}</MenuItem>
                  <MenuItem value="acknowledged">{t('statusAcknowledged')}</MenuItem>
                  <MenuItem value="resolved">{t('statusResolved')}</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Notifications Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              {notifications.map((notification) => (
            <Box key={notification.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: notification.isRead ? '1px solid var(--an-border)' : '2px solid var(--an-primary)',
                  backgroundColor: notification.isRead ? 'var(--an-white)' : 'rgb(var(--an-primary-rgb) / 0.05)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 'var(--an-shadow-md)',
                  },
                }}
                className="an-data-card"
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent sx={{ p: 2.5 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: notification.alertLevel === 'red' ? 'var(--an-error)' : 
                                          notification.alertLevel === 'yellow' ? 'var(--an-warning)' : 
                                          notification.alertLevel === 'green' ? 'var(--an-success)' : 'var(--an-neutral)',
                        }}
                      />
                      <Chip
                        label={notification.alertLevel?.toUpperCase() ?? 'INFO'}
                        color={getAlertLevelColor(notification.alertLevel) as any}
                        size="small"
                      />
                      <Chip
                        label={formatActionTypeLabel(notification.actionType)}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={t(`status${notification.status.charAt(0).toUpperCase()}${notification.status.slice(1)}`)}
                        color={getStatusColor(notification.status) as any}
                        size="small"
                      />
                    </Box>
                    {!notification.isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'var(--an-primary)',
                        }}
                      />
                    )}
                  </Box>

                  {/* Policy Name */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {notification.title || notification.policyName}
                  </Typography>
                  {notification.message && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {notification.message}
                    </Typography>
                  )}

                  {/* Detection Image Preview */}
                  <Box
                    sx={{
                      mb: 2,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid var(--an-border)',
                      backgroundColor: 'var(--an-background)',
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={notification.imageUrl}
                      alt={`Detection of ${notification.employeeName}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const errorMsg = document.createElement('div');
                        errorMsg.style.padding = '20px';
                        errorMsg.style.color = 'var(--an-text-tertiary)';
                        errorMsg.style.textAlign = 'center';
                        errorMsg.textContent = t('imageNotAvailable');
                        target.parentElement?.appendChild(errorMsg);
                      }}
                    />
                  </Box>

                  {/* Employee Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Person sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {notification.employeeName} ({notification.employeeId})
                    </Typography>
                  </Box>

                  {/* Camera Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Camera sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      {notification.camera}
                    </Typography>
                  </Box>

                  {/* Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessTime sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      {notification.detectionTime}
                    </Typography>
                  </Box>
                  {notification.acknowledgedAt && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                      <strong>{t('acknowledgedAt')}:</strong> {formatWorkflowDateTime(notification.acknowledgedAt)}
                    </Typography>
                  )}
                  {notification.resolvedAt && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                      <strong>{t('resolvedAt')}:</strong> {formatWorkflowDateTime(notification.resolvedAt)}
                    </Typography>
                  )}
                  {notification.resolutionNote && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>{t('resolveNote')}:</strong> {notification.resolutionNote}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {notification.status !== 'acknowledged' && notification.status !== 'resolved' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<i className="bi bi-check-lg"></i>}
                        onClick={(event) => handleAcknowledge(notification, event)}
                      >
                        {t('acknowledge')}
                      </Button>
                    )}
                    {notification.status !== 'resolved' && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<i className="bi bi-check2-circle"></i>}
                        onClick={(event) => handleResolveClick(notification, event)}
                      >
                        {t('resolve')}
                      </Button>
                    )}
                  </Box>

                </CardContent>
              </Card>
            </Box>
          ))}
            </Box>

            {/* Pagination */}
            {notifications.length > 0 && totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                className="an-pagination"
                />
                {totalCount > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('showingRange', { from: ((currentPage - 1) * perPage) + 1, to: Math.min(currentPage * perPage, totalCount), total: totalCount })}
                  </Typography>
                )}
              </Box>
            )}

            {/* Empty State */}
            {notifications.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {t('noNotifications')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('emptyHint')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
      </Container>


      {/* Notification Detail Modal */}
      <NotificationDetailModal
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        notification={selectedNotification}
        onAcknowledge={handleAcknowledge}
        onResolve={handleResolveClick}
      />
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('resolve')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('resolveNote')}
            placeholder={t('resolveNotePlaceholder')}
            value={resolutionNote}
            onChange={(event) => setResolutionNote(event.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={handleResolveSubmit} variant="contained">{t('resolve')}</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Notifications;

